import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Alert } from '@/components/UI/Alert';
import { Spinner } from '@/components/UI/Spinner';
import { apiErrorMessage } from '@/utils/apiError';
import {
  useListToolErrorTypesQuery,
  useListToolErrorsQuery,
  type ToolError,
} from '@/redux/internal-services/toolErrorsApi';
import { ToolErrorEditDialog } from './ToolErrorEditDialog';

/**
 * The catalogued errors one SAM tool can raise, with the same edit dialog as the Tool Errors page.
 * Edits change the shared error, so every agent that uses the tool sees them.
 */
export const ToolErrorsForTool: React.FC<{ toolName: string }> = ({ toolName }) => {
  const { t } = useTranslation();
  const errorsQuery = useListToolErrorsQuery({ tool: toolName }, { skip: !toolName });
  const typesQuery = useListToolErrorTypesQuery();
  const typeDefault = useMemo(
    () => Object.fromEntries((typesQuery.data?.data.types ?? []).map((x) => [x.type, x.retryable])),
    [typesQuery.data],
  );
  const [editing, setEditing] = useState<ToolError | null>(null);
  const errors = errorsQuery.data?.data ?? [];
  const yesNo = (v: boolean | undefined) => (v ? t('platform.toolErrors.yes') : t('platform.toolErrors.no'));

  return (
    <div className="space-y-2 border-t border-ui-border pt-3">
      <div>
        <p className="text-sm font-medium text-ui-text">{t('platform.toolErrors.agentSectionTitle')}</p>
        <p className="text-xs text-ui-text3">{t('platform.toolErrors.agentSectionHint')}</p>
      </div>

      {errorsQuery.isLoading ? (
        <Spinner size="sm" />
      ) : errorsQuery.isError ? (
        <Alert tone="danger">{apiErrorMessage(errorsQuery.error, t, t('platform.toolErrors.loadFailed'))}</Alert>
      ) : errors.length === 0 ? (
        <p className="text-xs text-ui-text3">{t('platform.toolErrors.agentSectionEmpty')}</p>
      ) : (
        <ul className="divide-y divide-ui-border rounded-control border border-ui-border">
          {errors.map((e) => (
            <li key={e.code} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-xs text-ui-text">{e.name}</span>
                  <Badge variant="accent" className="font-mono">{e.type}</Badge>
                  {e.type_from_exception && <span className="text-caption text-ui-text3">{t('platform.toolErrors.typeFromException')}</span>}
                  <span className="text-caption text-ui-text3">
                    {t('platform.toolErrors.retryable')}:{' '}
                    {e.retryable === null ? t('platform.toolErrors.inherits', { value: yesNo(typeDefault[e.type]) }) : yesNo(e.retryable)}
                  </span>
                  {!e.is_active && <Badge variant="neutral">{t('platform.toolErrors.inactive')}</Badge>}
                </div>
                <p className="mt-0.5 break-words font-mono text-xs text-ui-text2">{e.message}</p>
              </div>
              <Button type="button" variant="link" size="xs" onClick={() => setEditing(e)}>{t('platform.toolErrors.edit')}</Button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ToolErrorEditDialog
          error={editing}
          typeDefault={typeDefault}
          onClose={() => setEditing(null)}
          note={t('platform.toolErrors.sharedNote', { count: (editing.tools ?? []).length })}
        />
      )}
    </div>
  );
};

export default ToolErrorsForTool;
